import { ApiBase } from '@bymason/api-base';
import { DeviceModel } from '..';
import {
  CreateGroupOptions,
  ReadGroupOptions,
  GetGroupDeploymentsOptions,
  ListGroupsOptions,
  GroupModel,
  GroupDeploymentsModel,
  MoveDevicesToGroupOptions,
  MoveDevicesToGroupResult,
  GroupsPageableResult,
  GetGroupDevicesOptions,
  GroupPageableDevicesResult,
  UpdateGroupOptions,
  DeleteGroupResponse,
} from './types';

export * from './types';

class Groups extends ApiBase {
  private static instance: Groups;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns A Groups instance
   */
  public static getInstance(): Groups {
    if (!Groups.instance) {
      Groups.instance = new Groups();
    }

    return Groups.instance;
  }

  /**
   * Creates a device group.
   * @param options model data required to create a group.
   * @param clientToken token required for proper authorization.
   * @returns either a Group model or void
   */
  public async createGroup(options: CreateGroupOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deployment-groups');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GroupModel>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Deletes a device group.
   * @param options metadata required to delete a group.
   * @param clientToken token required for proper authorization.
   * @returns void
   */
  public async deleteGroup(options: ReadGroupOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deployment-groups/:id');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<DeleteGroupResponse>({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }

  /**
   * Updates a device group.
   * @param options metadata required to update a group.
   * @param clientToken token required for proper authorization.
   * @returns either a group model or void
   */
  public async updateGroup(options: UpdateGroupOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deployment-groups/:id');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GroupModel>({
      clientToken,
      method: 'PUT',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets a device group.
   * @param options metadata required to get a group.
   * @param clientToken token required for proper authorization.
   * @returns either a group model or void
   */
  public async readGroup(options: ReadGroupOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deployment-groups/:id');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GroupModel>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets a device group's deployed artifacts.
   * @param options metadata required to get a group deployment.
   * @param clientToken token required for proper authorization.
   * @returns either a group deploymet model or void
   */
  public async getGroupDeployments(options: GetGroupDeploymentsOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('group/:name/deployment');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GroupDeploymentsModel[]>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets a device group's associated devices.
   * @param options metadata required to get a group deployment.
   * @param clientToken token required for proper authorization.
   * @returns either a pageable devices model or void
   */
  public async getGroupDevices(options: GetGroupDevicesOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('group/:name/list');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GroupPageableDevicesResult<DeviceModel[] | null>>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets paginated device groups.
   * @param options metadata required to get groups.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes groups.
   */
  public async listGroups(options: ListGroupsOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deployment-groups');
    const url = this.expandUrlParams(urlTemplate, options, { addUnusedParamsToSearch: true });

    const dataResponse = await this.callJsonEndpoint<GroupsPageableResult<GroupModel[]>>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Moves devices into a device group.
   * @param options metadata required to move devices into a group.
   * @param clientToken token required for proper authorization.
   * @returns either void or a structure that describes result of operation.
   */
  public async moveDevicesToGroup(options: MoveDevicesToGroupOptions, clientToken: string) {
    const { groupName, validateOnly, ...body } = options;
    const urlTemplate = this.getEndpointTemplate('group/:groupName/move?validateonly=:validateOnly');
    const url = this.expandUrlParams(urlTemplate, { groupName, validateOnly });

    const dataResponse = await this.callJsonEndpoint<MoveDevicesToGroupResult>({
      body: JSON.stringify(body),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }
}

export const groupsClient = Groups.getInstance();
